import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import { communityAPI, linkPreviewAPI } from '../../util/api';
import { usersAPI, listingsAPI } from '../../api';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchCommunitySidebarData } from '../../util/communityTeam';
import { maybeOpenNewsletterPrompt } from '../../ducks/newsletterPrompt.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/PostPage/SET_INITIAL_STATE';

export const SHOW_POST_REQUEST = 'app/PostPage/SHOW_POST_REQUEST';
export const SHOW_POST_SUCCESS = 'app/PostPage/SHOW_POST_SUCCESS';
export const SHOW_POST_ERROR = 'app/PostPage/SHOW_POST_ERROR';
export const CURRENT_USER_VOTE_LOADED = 'app/PostPage/CURRENT_USER_VOTE_LOADED';

export const GET_LINK_PREVIEW_REQUEST = 'app/PostPage/GET_LINK_PREVIEW_REQUEST';
export const GET_LINK_PREVIEW_SUCCESS = 'app/PostPage/GET_LINK_PREVIEW_SUCCESS';
export const GET_LINK_PREVIEW_ERROR = 'app/PostPage/GET_LINK_PREVIEW_ERROR';

export const ADD_COMMENT_REQUEST = 'app/PostPage/ADD_COMMENT_REQUEST';
export const ADD_COMMENT_SUCCESS = 'app/PostPage/ADD_COMMENT_SUCCUESS';
export const ADD_COMMENT_ERROR = 'app/PostPage/ADD_COMMENT_ERROR';

export const DELETE_COMMENT_REQUEST = 'app/PostPage/DELETE_COMMENT_REQUEST';
export const DELETE_COMMENT_SUCCESS = 'app/PostPage/DELETE_COMMENT_SUCCUESS';
export const DELETE_COMMENT_ERROR = 'app/PostPage/DELETE_COMMENT_ERROR';

export const EDIT_COMMENT_REQUEST = 'app/PostPage/EDIT_COMMENT_REQUEST';
export const EDIT_COMMENT_SUCCESS = 'app/PostPage/EDIT_COMMENT_SUCCESS';
export const EDIT_COMMENT_ERROR = 'app/PostPage/EDIT_COMMENT_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/PostPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/PostPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/PostPage/CLOSE_LISTING_ERROR';

export const ADD_POST_COUNT_REQUEST = 'app/PostPage/ADD_POST_COUNT_REQUEST';
export const ADD_POST_COUNT_SUCCESS = 'app/PostPage/ADD_POST_COUNT_SUCCESS';
export const ADD_POST_COUNT_ERROR = 'app/PostPage/ADD_POST_COUNT_ERROR';

// New action types for fetching users for mentions
export const FETCH_USERS_FOR_MENTIONS_REQUEST = 'app/PostPage/FETCH_USERS_FOR_MENTIONS_REQUEST';
export const FETCH_USERS_FOR_MENTIONS_SUCCESS = 'app/PostPage/FETCH_USERS_FOR_MENTIONS_SUCCESS';
export const FETCH_USERS_FOR_MENTIONS_ERROR = 'app/PostPage/FETCH_USERS_FOR_MENTIONS_ERROR';

// Comment voting action types
export const VOTE_COMMENT_REQUEST = 'app/PostPage/VOTE_COMMENT_REQUEST';
export const VOTE_COMMENT_SUCCESS = 'app/PostPage/VOTE_COMMENT_SUCCESS';
export const VOTE_COMMENT_ERROR = 'app/PostPage/VOTE_COMMENT_ERROR';

// Right-sidebar bundle (team + member count) action types
export const FETCH_SIDEBAR_REQUEST = 'app/PostPage/FETCH_SIDEBAR_REQUEST';
export const FETCH_SIDEBAR_SUCCESS = 'app/PostPage/FETCH_SIDEBAR_SUCCESS';
export const FETCH_SIDEBAR_ERROR = 'app/PostPage/FETCH_SIDEBAR_ERROR';

// ================ Reducer ================ //

const initialState = {
  postId: null,
  showPostInProgress: false,
  showPostError: null,
  linkPreview: null,
  getLinkPreviewInProgress: false,
  getLinkPreviewError: null,
  addCommentInProgress: false,
  addCommentError: null,
  deletedCommentIds: [],
  currentDeleteCommentId: null,
  deleteCommentInProgress: false,
  deleteCommentError: null,
  editCommentInProgress: false,
  editCommentError: null,
  addPostCountInProgress: false,
  addPostCountError: null,
  voteCount: 0,
  currentUserVote: null,
  // New state for mention users
  mentionUsers: [],
  mentionUsersCache: null,
  mentionUsersCacheTimestamp: null,
  fetchMentionUsersInProgress: false,
  fetchMentionUsersError: null,
  // Comment voting state
  voteCommentInProgress: false,
  voteCommentError: null,
  currentListing: null,
  currentUser: null,
  // Right-sidebar
  sidebarTeam: [],
  sidebarMemberCount: 0,
  fetchSidebarInProgress: false,
  fetchSidebarError: null,
};

export default function postPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };

    case SHOW_POST_REQUEST:
      return {
        ...state,
        postId: payload.postId,
        showPostInProgress: true,
        showPostError: null,
      };
    case SHOW_POST_SUCCESS:
      return {
        ...state,
        showPostInProgress: false,
        showPostError: null,
        voteCount: payload.voteCount,
        currentUserVote: payload.currentUserVote,
        currentListing: payload.listing,
        currentUser: payload.currentUser,
      };
    case SHOW_POST_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showPostInProgress: false, showPostError: payload };
    case CURRENT_USER_VOTE_LOADED:
      return {
        ...state,
        currentUserVote: payload.currentUserVote,
        currentUser: payload.currentUser,
      };

    case GET_LINK_PREVIEW_REQUEST:
      return {
        ...state,
        getLinkPreviewInProgress: true,
        getLinkPreviewError: null,
      };
    case GET_LINK_PREVIEW_SUCCESS:
      return {
        ...state,
        linkPreview: payload.linkPreview,
        getLinkPreviewInProgress: false,
        getLinkPreviewError: null,
      };
    case GET_LINK_PREVIEW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, getLinkPreviewInProgress: false, getLinkPreviewError: payload };

    case ADD_COMMENT_REQUEST:
      return {
        ...state,
        addCommentInProgress: true,
        addCommentError: null,
      };
    case ADD_COMMENT_SUCCESS:
      // Update the currentListing with the new comment from the response
      const { comment: newComment } = payload;
      const currentComments = state.currentListing?.attributes?.publicData?.comments || [];

      return {
        ...state,
        addCommentInProgress: false,
        addCommentError: null,
        currentListing: state.currentListing
          ? {
              ...state.currentListing,
              attributes: {
                ...state.currentListing.attributes,
                publicData: {
                  ...state.currentListing.attributes.publicData,
                  comments: newComment ? [newComment, ...currentComments] : currentComments,
                },
              },
            }
          : null,
      };
    case ADD_COMMENT_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, addCommentInProgress: false, addCommentError: payload };

    case DELETE_COMMENT_REQUEST:
      return {
        ...state,
        currentDeleteCommentId: payload.commentId,
        deleteCommentInProgress: true,
        deleteCommentError: null,
      };
    case DELETE_COMMENT_SUCCESS:
      // Don't update the state here since addMarketplaceEntities already updated it
      // Just clear the loading state
      const { commentId } = payload;
      return {
        ...state,
        deletedCommentIds: [...(state.deletedCommentIds || []), commentId],
        deleteCommentInProgress: false,
        deleteCommentError: null,
        currentDeleteCommentId: null,
      };
    case DELETE_COMMENT_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, deleteCommentInProgress: false, deleteCommentError: payload };

    case EDIT_COMMENT_REQUEST:
      return {
        ...state,
        editCommentInProgress: true,
        editCommentError: null,
      };
    case EDIT_COMMENT_SUCCESS:
      // Update the specific comment in the current listing
      const { commentId: editedCommentId, updatedContent } = payload;
      const allComments = state.currentListing?.attributes?.publicData?.comments || [];
      const updatedComments = allComments.map(comment =>
        comment.id === editedCommentId ? { ...comment, content: updatedContent } : comment
      );

      return {
        ...state,
        editCommentInProgress: false,
        editCommentError: null,
        currentListing: state.currentListing
          ? {
              ...state.currentListing,
              attributes: {
                ...state.currentListing.attributes,
                publicData: {
                  ...state.currentListing.attributes.publicData,
                  comments: updatedComments,
                },
              },
            }
          : null,
      };
    case EDIT_COMMENT_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, editCommentInProgress: false, editCommentError: payload };

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: state.postId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.postId,
          error: payload,
        },
      };
    }

    case ADD_POST_COUNT_REQUEST:
      const { action } = payload;
      const { currentUserVote, voteCount } = state;

      let newVoteCount = voteCount;
      let newUserVote = null;

      // Case 1: No previous vote, adding new one
      if (!currentUserVote) {
        if (action === 'like') {
          newVoteCount = voteCount + 1;
          newUserVote = 'like';
        } else {
          newVoteCount = voteCount - 1;
          newUserVote = 'dislike';
        }
      }
      // Case 2: Clicking the same button again (toggling off)
      else if (currentUserVote === action) {
        if (action === 'like') {
          newVoteCount = voteCount - 1;
        } else {
          newVoteCount = voteCount + 1;
        }
        newUserVote = null;
      }
      // Case 3: SWITCHING from one vote type to another
      else {
        if (action === 'like') {
          newVoteCount = voteCount + 2; // remove dislike (-1) and add like (+1)
          newUserVote = 'like';
        } else {
          newVoteCount = voteCount - 2; // remove like (+1) and add dislike (-1)
          newUserVote = 'dislike';
        }
      }

      return {
        ...state,
        addPostCountInProgress: true,
        addPostCountError: null,
        voteCount: newVoteCount,
        currentUserVote: newUserVote,
      };
    case ADD_POST_COUNT_SUCCESS:
      return {
        ...state,
        addPostCountInProgress: false,
        addPostCountError: null,
      };
    case ADD_POST_COUNT_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, addPostCountInProgress: false, addPostCountError: payload };

    // New cases for mention users
    case FETCH_USERS_FOR_MENTIONS_REQUEST:
      return {
        ...state,
        fetchMentionUsersInProgress: true,
        fetchMentionUsersError: null,
      };
    case FETCH_USERS_FOR_MENTIONS_SUCCESS:
      return {
        ...state,
        mentionUsers: payload.users,
        mentionUsersCache: payload.users,
        mentionUsersCacheTimestamp: Date.now(),
        fetchMentionUsersInProgress: false,
        fetchMentionUsersError: null,
      };
    case FETCH_USERS_FOR_MENTIONS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        fetchMentionUsersInProgress: false,
        fetchMentionUsersError: payload,
      };

    // Comment voting cases
    case VOTE_COMMENT_REQUEST: {
      const { commentId, action } = payload;
      const comments = state.currentListing?.attributes?.publicData?.comments || [];
      const commentIndex = comments.findIndex(c => c.id === commentId);

      if (commentIndex === -1) return state;

      const comment = comments[commentIndex];
      const currentUserId = state.currentUser?.id?.uuid;
      const existingReaction = comment.reactions?.find(r => r.userId === currentUserId);

      let newVoteCount = comment.voteCount || 0;
      let newUserVote = null;

      // Exact same logic as post voting
      if (!existingReaction) {
        // Case 1: No previous vote, adding new one
        if (action === 'like') {
          newVoteCount = newVoteCount + 1;
          newUserVote = 'like';
        } else {
          newVoteCount = newVoteCount - 1;
          newUserVote = 'dislike';
        }
      } else if (existingReaction.type === action) {
        // Case 2: Clicking the same button again (toggling off)
        if (action === 'like') {
          newVoteCount = newVoteCount - 1;
        } else {
          newVoteCount = newVoteCount + 1;
        }
        newUserVote = null;
      } else {
        // Case 3: SWITCHING from one vote type to another
        if (action === 'like') {
          newVoteCount = newVoteCount + 2; // remove dislike (-1) and add like (+1)
          newUserVote = 'like';
        } else {
          newVoteCount = newVoteCount - 2; // remove like (+1) and add dislike (-1)
          newUserVote = 'dislike';
        }
      }

      // Simple optimistic update - just update vote count and current user's reaction
      const updatedComments = [...comments];
      const updatedReactions = comment.reactions
        ? [...comment.reactions.filter(r => r.userId !== currentUserId)]
        : [];

      if (newUserVote) {
        updatedReactions.push({
          id: `${commentId}-${currentUserId}-${Date.now()}`,
          userId: currentUserId,
          type: newUserVote,
          createdAt: new Date().toISOString(),
        });
      }

      updatedComments[commentIndex] = {
        ...comment,
        voteCount: newVoteCount,
        reactions: updatedReactions,
      };

      return {
        ...state,
        voteCommentInProgress: true,
        voteCommentError: null,
        currentListing: state.currentListing
          ? {
              ...state.currentListing,
              attributes: {
                ...state.currentListing.attributes,
                publicData: {
                  ...state.currentListing.attributes.publicData,
                  comments: updatedComments,
                },
              },
            }
          : null,
      };
    }

    case VOTE_COMMENT_SUCCESS: {
      return {
        ...state,
        voteCommentInProgress: false,
        voteCommentError: null,
      };
    }

    case VOTE_COMMENT_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        voteCommentInProgress: false,
        voteCommentError: payload,
      };

    case FETCH_SIDEBAR_REQUEST:
      return { ...state, fetchSidebarInProgress: true, fetchSidebarError: null };
    case FETCH_SIDEBAR_SUCCESS:
      return {
        ...state,
        fetchSidebarInProgress: false,
        sidebarTeam: payload?.team || [],
        sidebarMemberCount: payload?.memberCount || 0,
      };
    case FETCH_SIDEBAR_ERROR:
      return { ...state, fetchSidebarInProgress: false, fetchSidebarError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showPostRequest = postId => ({
  type: SHOW_POST_REQUEST,
  payload: { postId },
});
export const showPostSuccess = (voteCount, currentUserVote, listing, currentUser) => ({
  type: SHOW_POST_SUCCESS,
  payload: { voteCount, currentUserVote, listing, currentUser },
});
export const showPostError = e => ({
  type: SHOW_POST_ERROR,
  error: true,
  payload: e,
});
export const currentUserVoteLoaded = (currentUserVote, currentUser) => ({
  type: CURRENT_USER_VOTE_LOADED,
  payload: { currentUserVote, currentUser },
});

// The signed-in user's vote on the post, from the reactions in its metadata.
const currentUserVoteOf = (post, currentUser) => {
  const reactions = post?.attributes?.metadata?.reactions || [];
  return reactions.find(r => r.userId === currentUser?.id?.uuid)?.type || null;
};

export const getLinkPreviewRequest = () => ({
  type: GET_LINK_PREVIEW_REQUEST,
});
export const getLinkPreviewSuccess = linkPreview => ({
  type: GET_LINK_PREVIEW_SUCCESS,
  payload: { linkPreview },
});
export const getLinkPreviewError = e => ({
  type: GET_LINK_PREVIEW_ERROR,
  error: true,
  payload: e,
});

export const addCommentRequest = () => ({
  type: ADD_COMMENT_REQUEST,
});
export const addCommentSuccess = comment => ({
  type: ADD_COMMENT_SUCCESS,
  payload: { comment },
});
export const addCommentError = e => ({
  type: ADD_COMMENT_ERROR,
  error: true,
  payload: e,
});

export const deleteCommentRequest = commentId => ({
  type: DELETE_COMMENT_REQUEST,
  payload: { commentId },
});
export const deleteCommentSuccess = (commentId, hasReplies) => ({
  type: DELETE_COMMENT_SUCCESS,
  payload: { commentId, hasReplies },
});
export const deleteCommentError = e => ({
  type: DELETE_COMMENT_ERROR,
  error: true,
  payload: e,
});

export const editCommentRequest = () => ({
  type: EDIT_COMMENT_REQUEST,
});
export const editCommentSuccess = (commentId, updatedContent) => ({
  type: EDIT_COMMENT_SUCCESS,
  payload: { commentId, updatedContent },
});
export const editCommentError = e => ({
  type: EDIT_COMMENT_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const addPostCountRequest = action => ({
  type: ADD_POST_COUNT_REQUEST,
  payload: { action },
});
export const addPostCountSuccess = () => ({
  type: ADD_POST_COUNT_SUCCESS,
});
export const addPostCountError = e => ({
  type: ADD_POST_COUNT_ERROR,
  error: true,
  payload: e,
});

// New action creators for mention users
export const fetchUsersForMentionsRequest = () => ({
  type: FETCH_USERS_FOR_MENTIONS_REQUEST,
});
export const fetchUsersForMentionsSuccess = users => ({
  type: FETCH_USERS_FOR_MENTIONS_SUCCESS,
  payload: { users },
});
export const fetchUsersForMentionsError = e => ({
  type: FETCH_USERS_FOR_MENTIONS_ERROR,
  error: true,
  payload: e,
});

// Comment voting action creators
export const voteCommentRequest = (commentId, action) => ({
  type: VOTE_COMMENT_REQUEST,
  payload: { commentId, action },
});
export const voteCommentSuccess = (commentId, voteCount, currentUserVote) => ({
  type: VOTE_COMMENT_SUCCESS,
  payload: { commentId, voteCount, currentUserVote },
});
export const voteCommentError = e => ({
  type: VOTE_COMMENT_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const showPost = postId => (dispatch, getState, sdk) => {
  dispatch(showPostRequest(postId));

  return sdk.listings
    .show({
      id: postId,
      include: ['images', 'author', 'author.profileImage'],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.publicData',
        'profile.metadata',
      ],
      'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      // Get link preview
      const linkUrl = response?.data?.data?.attributes?.publicData?.linkUrl;
      if (linkUrl) {
        dispatch(getLinkPreview(linkUrl));
      }

      // Get comments
      const comments = response.data.data.attributes.publicData.comments;
      if (comments) {
        const { currentUser } = getState().user;
        const currentUserId = currentUser?.id?.uuid || null;

        // Extract unique user IDs excluding the current user
        const uniqueUserIds = [...new Set(comments.map(comment => comment.userId))].filter(
          id => id && id !== currentUserId
        );

        // Only make the API call if there are users to fetch
        if (uniqueUserIds.length > 0) {
          usersAPI
            .query({
              pub_id: uniqueUserIds,
              include: ['profileImage'],
              'fields.user': [
                'profile.displayName',
                'profile.abbreviatedName',
                'profile.publicData',
                'profile.metadata',
              ],
              'fields.image': ['variants.square-small', 'variants.square-small2x'],
            })
            .then(response => dispatch(addMarketplaceEntities(response.data)))
            .catch(error => {
              console.error('Failed to fetch users:', error);
            });
        }
      }

      // Get vote count
      const voteCount = response.data.data.attributes.metadata.voteCount || 0;
      const { currentUser } = getState().user;
      const currentUserVote = currentUserVoteOf(response.data.data, currentUser);
      dispatch(showPostSuccess(voteCount, currentUserVote, response.data.data, currentUser));

      // Add marketplace entities
      dispatch(addMarketplaceEntities(response));

      return response;
    })
    .catch(e => dispatch(showPostError(storableError(e))));
};

export const getLinkPreview = linkUrl => async (dispatch, getState, sdk) => {
  dispatch(getLinkPreviewRequest());

  try {
    const response = await linkPreviewAPI.getLinkPreview({ url: linkUrl });
    const linkPreview = response.data;

    dispatch(getLinkPreviewSuccess(linkPreview));
    return linkPreview;
  } catch (error) {
    dispatch(getLinkPreviewError(storableError(error)));
    throw error;
  }
};

export const addComment = params => (dispatch, getState, sdk) => {
  dispatch(addCommentRequest());

  return communityAPI.comments
    .create(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));

      // The API returns the updated listing, so we extract the latest comments
      const updatedListing = response.data?.data;
      const comments = updatedListing?.attributes?.publicData?.comments || [];
      // The new comment is the first one (since it's added to the beginning)
      const newComment = comments.length > 0 ? comments[0] : null;

      dispatch(addCommentSuccess(newComment));

      return response;
    })
    .catch(e => {
      dispatch(addCommentError(storableError(e)));
    });
};

export const deleteComment = params => (dispatch, getState, sdk) => {
  dispatch(deleteCommentRequest(params.commentId));

  return communityAPI.comments
    .delete(params)
    .then(response => {
      // Get the updated comments from the response to determine if it was soft or hard deleted
      const updatedListing = response.data?.data;
      const updatedComments = updatedListing?.attributes?.publicData?.comments || [];

      // Check if the comment still exists (meaning it was soft deleted due to replies)
      const deletedComment = updatedComments.find(c => c.id === params.commentId);
      const hasReplies = deletedComment && deletedComment.isDeleted;

      dispatch(deleteCommentSuccess(params.commentId, hasReplies));

      return response;
    })
    .catch(e => {
      dispatch(deleteCommentError(storableError(e)));
    });
};

export const editComment = (commentId, updatedContent, listingId) => (dispatch, getState, sdk) => {
  dispatch(editCommentRequest());

  return communityAPI.comments
    .update({ commentId, content: updatedContent, listingId })
    .then(response => {
      dispatch(editCommentSuccess(commentId, updatedContent));
      return response;
    })
    .catch(e => {
      dispatch(editCommentError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const addPostCount = params => (dispatch, getState, sdk) => {
  dispatch(addPostCountRequest(params.action));

  return communityAPI.posts
    .addCount(params)
    .then(response => {
      dispatch(addPostCountSuccess());
      return response;
    })
    .catch(e => {
      dispatch(addPostCountError(storableError(e)));
    });
};

// New thunk for fetching users for mentions
export const fetchUsersForMentions = (forceRefresh = false) => (dispatch, getState, sdk) => {
  const state = getState();
  const { mentionUsersCache, mentionUsersCacheTimestamp } = state.PostPage;

  // Check if we have valid cache (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;
  const now = Date.now();
  const isCacheValid =
    mentionUsersCache &&
    mentionUsersCacheTimestamp &&
    now - mentionUsersCacheTimestamp < CACHE_DURATION;

  if (isCacheValid && !forceRefresh) {
    // Return cached data immediately
    return Promise.resolve(mentionUsersCache);
  }

  dispatch(fetchUsersForMentionsRequest());

  return usersAPI
    .query({
      per_page: 100, // Get maximum allowed users
      include: ['profileImage'],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.firstName',
        'profile.lastName',
        'profile.publicData',
      ],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const users = denormalisedResponseEntities(response.data);

      dispatch(fetchUsersForMentionsSuccess(users));
      return users;
    })
    .catch(e => {
      dispatch(fetchUsersForMentionsError(storableError(e)));
      throw e;
    });
};

/**
 * Search users locally for mentions - instant results!
 * This function works with the cached users from Redux state
 *
 * @param {Array} allUsers - All users from Redux state
 * @param {string} searchTerm - What the user is typing after @
 * @param {number} maxResults - Maximum number of results to return (default: 5)
 * @returns {Array} Filtered and sorted users
 */
/**
 * Search users locally for mentions - works with raw user objects from API
 *
 * @param {Array} allUsers - Raw user objects from Redux state
 * @param {string} searchTerm - What the user is typing after @
 * @param {number} maxResults - Maximum number of results to return (default: 5)
 * @returns {Array} Filtered and sorted raw user objects
 */
export const searchUsersForMentions = (allUsers, searchTerm, maxResults = 5) => {
  if (!allUsers || allUsers.length === 0) {
    return [];
  }

  if (!searchTerm || searchTerm.length === 0) {
    // Return first few users if no search term
    return allUsers.slice(0, maxResults);
  }

  const searchLower = searchTerm.toLowerCase();

  // Filter and score users based on search relevance
  const scoredUsers = allUsers
    .map(user => {
      const profile = user.attributes.profile;
      const displayName =
        profile.displayName ||
        `${profile.firstName || ''} ${profile.lastName || ''}`.trim() ||
        'Unknown User';
      const firstName = profile.firstName || '';
      const lastName = profile.lastName || '';
      const searchText = `${displayName} ${firstName} ${lastName}`.toLowerCase();

      let score = 0;

      // Exact match at start of display name (highest priority)
      if (displayName.toLowerCase().startsWith(searchLower)) {
        score += 100;
      }
      // Exact match at start of first name
      else if (firstName.toLowerCase().startsWith(searchLower)) {
        score += 90;
      }
      // Exact match at start of last name
      else if (lastName.toLowerCase().startsWith(searchLower)) {
        score += 80;
      }
      // Contains search term in display name
      else if (displayName.toLowerCase().includes(searchLower)) {
        score += 50;
      }
      // Contains search term anywhere in searchable text
      else if (searchText.includes(searchLower)) {
        score += 30;
      }

      return { user, score };
    })
    .filter(item => item.score > 0) // Only keep matches
    .sort((a, b) => b.score - a.score) // Sort by relevance
    .slice(0, maxResults) // Limit results
    .map(item => item.user); // Return just the raw user objects

  return scoredUsers;
};

export const fetchSidebarRequest = () => ({ type: FETCH_SIDEBAR_REQUEST });
export const fetchSidebarSuccess = ({ team, memberCount }) => ({
  type: FETCH_SIDEBAR_SUCCESS,
  payload: { team, memberCount },
});
export const fetchSidebarError = e => ({
  type: FETCH_SIDEBAR_ERROR,
  error: true,
  payload: e,
});

// Resolves the 6 team emails + total member count via the existing
// /api/users/query endpoint. Errors are swallowed so the rest of
// the post still renders if the bundle fails.
export const fetchCommunitySidebar = () => async dispatch => {
  dispatch(fetchSidebarRequest());
  try {
    const data = await fetchCommunitySidebarData();
    dispatch(fetchSidebarSuccess(data));
  } catch (e) {
    dispatch(fetchSidebarError(storableError(e)));
  }
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const postId = new UUID(params.id);
  // Newsletter prompt is self-gated; fire-and-forget.
  dispatch(maybeOpenNewsletterPrompt());
  // No longer preload users for mentions - we use on-demand search now
  return Promise.all([dispatch(showPost(postId)), dispatch(fetchCommunitySidebar())]);
};

// The visitor's own part of the page, loaded in the browser after server rendering (see
// onlyClientDataNeeded in src/Routes.js): the newsletter prompt and their vote on the post,
// which the server could not know. The post itself is already in the state.
export const loadClientData = () => (dispatch, getState) => {
  dispatch(maybeOpenNewsletterPrompt());
  const { currentListing } = getState().PostPage;
  const { currentUser } = getState().user;
  dispatch(currentUserVoteLoaded(currentUserVoteOf(currentListing, currentUser), currentUser));
};

// Comment voting thunk
export const voteComment = (commentId, action, listingId) => (dispatch, getState, sdk) => {
  dispatch(voteCommentRequest(commentId, action));

  return communityAPI.comments
    .addCount({ commentId, action, listingId })
    .then(response => {
      dispatch(voteCommentSuccess(commentId, response.voteCount, response.currentUserVote));
      return response;
    })
    .catch(e => {
      dispatch(voteCommentError(storableError(e)));
      throw e;
    });
};
